<template lang="pug">
form.simulator(@submit.prevent="calculate")
  //-
  //- Financial Instrument Type
  //-
  .form__field
    label(for="type")
      | {{ $t('simulator.form.financialInstrumentLabel') }}

    .radio-group.grid--four--columns
      .radio-item
        input(
          id="equity"
          v-model="financialInstrument"
          type="radio"
          value="equity"
          hidden)
        label(for="equity")
          //- img(src="/images/ico_sim_equity.png" alt="Equity")
          span {{ $t('simulator.form.equity') }}

      .radio-item
        input(
          id="reit"
          v-model="financialInstrument"
          type="radio"
          value="reit"
          hidden)
        label(for="reit")
          //- img(src="/images/ico_sim_socimi.png" alt="Reit")
          span {{ $t('simulator.form.reit') }}

      .radio-item
        input(
          id="debt"
          v-model="financialInstrument"
          type="radio"
          value="debt"
          hidden)
        label(for="debt")
          //- img(src="/images/ico_sim_debt.png" alt="Debt")
          span {{ $t('simulator.form.debt') }}

      .radio-item
        input(
          id="fund"
          v-model="financialInstrument"
          type="radio"
          value="fund"
          hidden)
        label(for="fund")
          //- img(src="/images/ico_sim_found.png" alt="Fund")
          span {{ $t('simulator.form.fund') }}

  //-
  //- Note
  //- Financial instruments not included in the simulator require complex calculations.
  //-  In these cases, the user should be directed to use the contact form to request a simulation.
  //-
  div(v-if="!hasCostSimulator && financialInstrument")
    .form__note.margin-bottom
      | {{ $t('simulator.form.noSimulator') }}

    NuxtLinkLocale.button(to="/portfolio-fees/#contact") {{ $t('simulator.form.contactUs') }}

  //-
  //- Migration
  //-
  .form__field(v-if="hasCostSimulator")
    label(for="issuanceType")
      | {{ $t('simulator.form.issuanceTypeLabel') }}

    .radio-group.radio-item--center.grid--two--columns
      .radio-item
        input(
          id="newIssuance"
          v-model="issuanceType"
          type="radio"
          value="new"
          hidden)
        label.padding-top-half.padding-bottom-half(for="newIssuance") {{ $t('simulator.form.newListing') }}

      .radio-item
        input(
          id="migrationIssuance"
          v-model="issuanceType"
          type="radio"
          value="migration"
          hidden)
        label.padding-top-half.padding-bottom-half(for="migrationIssuance") {{ $t('simulator.form.migrationListing') }}

    .form__note(v-if="issuanceType === 'migration'")
      strong.margin-right-quarter {{ $t('simulator.form.note') }}
      | {{ $t('simulator.form.migrationNote') }}

  //-
  //- Amount
  //-
  .form__field(v-if="hasCostSimulator && issuanceType")
    label(for="amount-input")
      | {{ $t('simulator.form.amountLabel') }}

    .form__amount
      InputAmount(
        v-model="amount"
        input-id="amount-input"
        :label="$t('simulator.form.amountLabel')"
        :locale="locale"
        :aria-label="$t('simulator.form.amountLabel')"
        currency="EUR"
        is-integer)

      .button(@click="calculate" ) {{ $t('simulator.form.calculate') }}

    .form__error(v-if="error")
      | {{ error }}
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import InputAmount from '~/components/InputAmount.vue'
  import NuxtLinkLocale from '~/components/NuxtLinkLocale.vue'
  import type { FinancialInstrument, IssuanceType } from '../types'

  interface FormSimulatorEmits {
    calculate: []
  }

  //
  // Props and events
  //
  const emit = defineEmits<FormSimulatorEmits>()

  //
  // Composables
  //
  const { locale, t } = useI18n()

  //
  // Data
  //
  const financialInstrument = defineModel<FinancialInstrument>('financialInstrument')
  const issuanceType = defineModel<IssuanceType>('issuanceType')
  const amount = defineModel<number>('amount', { type: Number, default: 0 })
  const error = defineModel<string>('error', { default: '' })
  //
  // Computed
  //
  const hasCostSimulator = computed(() => {
    if (!financialInstrument.value) return false
    return ['equity', 'reit'].includes(financialInstrument.value)
  })

  //
  // Methods
  //
  function calculate() {
    error.value = ''

    if (!amount.value) {
      error.value = t('simulator.form.amountRequired')
      return
    }

    emit('calculate')
  }
</script>

<style lang="scss" scoped>
  .simulator {
    background-color: $color-background;
    border-radius: $border-radius-big;
    padding: double();
  }

  .form__field {
    &:not(:last-child) {
      margin-bottom: double();
    }

    > label {
      margin-bottom: simple();
      font-weight: font-weight(semibold);
    }
  }

  .form__amount {
    display: flex;
    align-items: center;
    gap: simple();
  }

  .form__note {
    font-size: font-size(small1);
    color: color(gray4);
    margin-top: half();
    font-style: italic;
    max-width: 30rem;
  }

  .form__error {
    font-size: font-size(small1);
    color: color(danger);
    margin-top: half();
  }

  .radio-group {
    width: 100%;
    row-gap: half();
  }

  .radio-item {
    label {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: half();
      cursor: pointer;
      background-color: color(white);
      padding: simple() simple();
      border-radius: $border-radius;
      border: 1px solid color(gray8);
      font-size: font-size(small1);
      font-weight: font-weight(semibold);
      text-align: center;
      justify-content: center;
      min-height: 5rem;
      height: 100%;

      img {
        height: 3.5rem;
      }
    }

    label:hover {
      border-color: color(brand);
    }

    input[type='radio']:checked + label {
      border-color: color(brand);
      background-color: #e7f0fd;
    }
  }

  .radio-item--center {
    label {
      justify-content: center;
    }
  }

  @include media(palm) {
    .simulator {
      padding: simple();
    }

    .form__amount {
      flex-direction: column;
      align-items: normal;
    }
  }
</style>
